import React from 'react';

import { EditorState } from 'draft-js';
import styled from 'styled-components';

import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme, { getInteractiveStyles } from '@adsk/alloy-react-theme';

import MentionPlugin, { MentionPluginProps } from './MentionPlugin';

type Booleanish = boolean | 'true' | 'false';

type TextareaProps = {
  error?: string;
  disabled?: boolean;
  focused?: boolean;
  maxHeight?: number | string;
  minHeight?: number | string;
};

export type MentionTextareaProps = Omit<MentionPluginProps, 'spellCheck'> &
  TextareaProps & {
    spellCheck?: Booleanish;
  };

const StyledMentionPlugin = styled(MentionPlugin)<TextareaProps>`
  ${theme.typography.labelMedium};
  color: ${theme.colors.charcoal900};
  border: 1px solid ${theme.colors.charcoal400};
  border-radius: 2px;
  box-sizing: border-box;
  max-height: ${({ maxHeight }) => maxHeight};
  min-height: ${({ minHeight }) => minHeight};
  outline: none;
  padding: 8px 12px;
  overflow: auto;

  ${({ disabled }) => getInteractiveStyles(!!disabled)};

  ${({ disabled }) =>
    disabled &&
    `
    background: ${theme.colors.charcoal050};
    border-color: ${theme.colors.charcoal200};
    box-shadow: none;
    color: ${theme.colors.charcoal500};
    cursor: not-allowed;
    overflow: hidden;
  `};

  ${({ error }: TextareaProps) =>
    !error &&
    `&:focus:not(:active) { border-color: ${theme.colors.adskBlue500}}`};

  ${({ error }: TextareaProps) =>
    error && `border-color: ${theme.colors.red600}`};

  ${({ focused }: TextareaProps) =>
    focused && `box-shadow: 0 0 0 2px ${theme.colors.focusHalo}`};
`;

const MentionTextarea = ({
  className,
  style,
  focused,
  error,
  spellCheck,
  disabled = false,
  maxHeight = '100%',
  minHeight = 36,
  ...props
}: MentionTextareaProps) => (
  <StyledMentionPlugin
    {...props}
    className={className}
    readOnly={disabled}
    disabled={!!disabled}
    error={error}
    focused={focused}
    maxHeight={maxHeight}
    minHeight={minHeight}
    style={style}
  />
);

MentionTextarea.displayName = 'MentionTextarea';

export default Object.assign(WithAlloySuspense(MentionTextarea), {
  PLACEMENTS: MentionPlugin.PLACEMENTS,
  EMPTY_STATE: EditorState.createEmpty(),
  TYPES: MentionPlugin.TYPES,
});
